import { useState, useEffect, useMemo, useRef } from "react";
import {
  Button,
  GroupTextField,
  GroupTextFieldRow,
  Input,
} from "@elaniintc/goes-design-system";
import { VariableSelector } from "./VariableSelector";
import { OperationSelector } from "./OperationSelector";
import { useCondition } from "./useCondition";
import ComparativeValueInputCalendar from "./ComparativeValueInput.calendar";
import type { Operation, Variable } from "./types";

export type ConditionData = {
  selectedValue?: {
    id?: string;
    name?: string;
    type?: string;
  };
  selectedOperation?: {
    name: string;
    label: string;
    needsComparativeValue: boolean;
    comparativeValueType: string;
  };
  compareValueType?: string; // "inp" | "var"
  compareInput?: string; // siempre string
  compareDate?: string | Date;
  selectedVar?: {
    id: string;
    name: string;
    type: string;
  };
};

export type VisibilityConditionProps = {
  variableList: Variable[];
  initialData?: ConditionData;
  onChange: (data: ConditionData) => void;
  onValidityChange?: (isValid: boolean) => void;
};

const shallowEqual = <T extends Record<string, unknown>>(
  a: T | undefined,
  b: T | undefined,
): boolean => {
  if (Object.is(a, b)) return true;
  if (typeof a !== "object" || typeof b !== "object" || !a || !b) return false;

  // Date por valor
  if (a instanceof Date || b instanceof Date) {
    return (
      a instanceof Date && b instanceof Date && a.getTime() === b.getTime()
    );
  }

  const ak = Object.keys(a) as (keyof T)[];
  const bk = Object.keys(b) as (keyof T)[];
  if (ak.length !== bk.length) return false;

  for (const k of ak) {
    const va = a[k];
    const vb = b[k];

    if (va instanceof Date || vb instanceof Date) {
      if (
        !(
          va instanceof Date &&
          vb instanceof Date &&
          va.getTime() === vb.getTime()
        )
      ) {
        return false;
      }
    } else if (!Object.is(va, vb)) {
      return false;
    }
  }
  return true;
};

export const VisibilityCondition: React.FC<VisibilityConditionProps> = ({
  variableList,
  initialData,
  onChange,
  onValidityChange,
}) => {
  const {
    selectedVariable,
    selectedOperation: selectedOperationCondition,
    handleVariableChange,
    handleOperationChange,
  } = useCondition();

  const [conditionerType, setConditionerType] = useState<"inp" | "var">(
    initialData?.compareValueType === "var" ? "var" : "inp",
  );

  const [inputField, setInputField] = useState<string>(
    initialData?.compareInput ?? "",
  );
  const [dateValue, setDateValue] = useState<Date | string | undefined>(
    initialData?.compareDate,
  );

  const prevVarIdRef = useRef<string | undefined>(undefined);
  const prevOpNameRef = useRef<string | undefined>(undefined);
  const userChangeRef = useRef(false);

  const [showWarning, setShowWarning] = useState(false);

  const didHydrateRef = useRef(false);
  useEffect(() => {
    if (!initialData || didHydrateRef.current) return;

    const existsInList = initialData.selectedValue
      ? variableList.some((v) => v.name === initialData.selectedValue?.name)
      : false;

    if (
      !existsInList &&
      initialData.selectedValue?.name &&
      initialData.selectedValue.name.length > 0
    ) {
      // Resetear todo si no coincide
      handleVariableChange(null as unknown as Variable);
      handleOperationChange(null as unknown as Operation);
      setConditionerType("inp");
      setInputField("");
      setShowWarning(true);
      setDateValue(undefined);

      prevVarIdRef.current = undefined;
      prevOpNameRef.current = undefined;

      didHydrateRef.current = true;
      return;
    }

    // Si existe, hidratar normalmente
    if (initialData.selectedValue) {
      handleVariableChange(initialData.selectedValue as Variable);
    }
    if (initialData.selectedOperation) {
      handleOperationChange(initialData.selectedOperation as Operation);
    }
    if (typeof initialData.compareValueType !== "undefined") {
      setConditionerType(
        initialData.compareValueType === "var" ? "var" : "inp",
      );
    }
    if (typeof initialData.compareInput !== "undefined") {
      setInputField(String(initialData.compareInput));
    }
    if (typeof initialData.compareDate !== "undefined") {
      setDateValue(initialData.compareDate);
    }

    prevVarIdRef.current = initialData.selectedValue?.id;
    prevOpNameRef.current = initialData.selectedOperation?.name;

    didHydrateRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialData, variableList]);

  // Resetear SOLO si el cambio viene del usuario
  useEffect(() => {
    const currVarId = selectedVariable?.id;
    const currOpName = selectedOperationCondition?.name;

    const varChanged = prevVarIdRef.current !== currVarId;
    const opChanged = prevOpNameRef.current !== currOpName;

    if (userChangeRef.current && (varChanged || opChanged)) {
      setInputField("");
      setDateValue(undefined);
      userChangeRef.current = false;
    }

    prevVarIdRef.current = currVarId;
    prevOpNameRef.current = currOpName;
  }, [selectedVariable?.id, selectedOperationCondition?.name]);

  const onUserSelectVariable = (v: Variable | null) => {
    userChangeRef.current = true;
    if (v) {
      handleVariableChange(v);
    } else {
      setInputField("");
      setDateValue(undefined);
      prevVarIdRef.current = undefined;
    }
  };

  const onUserSelectOperation = (o: Operation | null) => {
    userChangeRef.current = true;
    if (o) {
      handleOperationChange(o);
    } else {
      setInputField("");
      setDateValue(undefined);
      prevOpNameRef.current = undefined;
    }
  };

  const normalizedOperation = useMemo(() => {
    if (!selectedOperationCondition) return undefined;
    return {
      name: selectedOperationCondition.name,
      label: selectedOperationCondition.label,
      needsComparativeValue: Boolean(
        selectedOperationCondition.needsComparativeValue,
      ),
      comparativeValueType:
        selectedOperationCondition.comparativeValueType ??
        selectedVariable?.type ??
        "string",
    };
  }, [selectedOperationCondition, selectedVariable?.type]);

  const needsComparative =
    normalizedOperation?.name === "EQUALS" ||
    normalizedOperation?.name === "NOT_EQUALS" ||
    normalizedOperation?.name === "IS_AFTER" ||
    normalizedOperation?.name === "IS_BEFORE" ||
    normalizedOperation?.name === "IS_ON_OR_AFTER" ||
    normalizedOperation?.name === "IS_ON_OR_BEFORE" ||
    normalizedOperation?.name === "GREATER_THAN" ||
    normalizedOperation?.name === "GREATER_THAN_OR_EQUAL" ||
    normalizedOperation?.name === "LESS_THAN_OR_EQUAL" ||
    normalizedOperation?.name === "LESS_THAN" ||
    normalizedOperation?.name === "STARTS_WITH" ||
    normalizedOperation?.name === "ENDS_WITH" ||
    normalizedOperation?.name === "CONTAINS" ||
    normalizedOperation?.name === "NOT_CONTAINS";

  const isNonEmpty = (v: unknown) => {
    if (v === null || v === undefined) return false;
    if (typeof v === "string") return v.trim().length > 0;
    return true;
  };

  const isValidDateValue = (v: Date | string | undefined) => {
    if (!v) return false;
    if (v instanceof Date) return !Number.isNaN(v.getTime());
    const d = new Date(v);
    return !Number.isNaN(d.getTime());
  };

  const isValid = useMemo(() => {
    if (!selectedVariable) return false;
    if (!normalizedOperation) return false;

    if (!needsComparative) return true;

    if (conditionerType === "inp") {
      if (
        selectedVariable.type === "datetime" ||
        selectedVariable.type === "datetime_range" ||
        selectedVariable.type === "date" ||
        selectedVariable.type === "date_range"
      ) {
        return isValidDateValue(dateValue);
      }

      return isNonEmpty(inputField);
    } else {
      return isNonEmpty(inputField);
    }
  }, [
    selectedVariable,
    normalizedOperation,
    needsComparative,
    conditionerType,
    inputField,
    dateValue,
  ]);

  // Emitir al padre solo si cambió realmente (payload nuevo type)
  const lastPayloadRef = useRef<ConditionData | undefined>(undefined);
  useEffect(() => {
    const data: ConditionData = {
      selectedValue: selectedVariable
        ? {
            id: selectedVariable.id,
            name: selectedVariable.name,
            type: selectedVariable.type,
          }
        : undefined,
      selectedOperation: normalizedOperation ?? undefined,
      compareValueType: conditionerType,
      compareDate: dateValue,
      compareInput:
        typeof inputField === "string" ? inputField : String(inputField ?? ""),
      selectedVar: selectedVariable
        ? {
            id: selectedVariable.id,
            name: selectedVariable.name,
            type: selectedVariable.type,
          }
        : undefined,
    };

    if (
      !lastPayloadRef.current ||
      !shallowEqual(lastPayloadRef.current, data)
    ) {
      lastPayloadRef.current = data;
      onChange(data);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedVariable,
    normalizedOperation,
    conditionerType,
    inputField,
    dateValue,
  ]);

  const lastValidityRef = useRef<boolean | null>(null);
  useEffect(() => {
    if (!onValidityChange) return;
    if (
      lastValidityRef.current === null ||
      lastValidityRef.current !== isValid
    ) {
      lastValidityRef.current = isValid;
      onValidityChange(isValid);
    }
  }, [isValid, onValidityChange]);

  // No mostrar campos de interoperabilidad
  const filteredVariableList = useMemo(
    () => variableList.filter((v) => v.type !== "interoperability"),
    [variableList],
  );

  return (
    <GroupTextField label="Condición" required className="w-full">
      <GroupTextFieldRow>
        <VariableSelector
          variables={filteredVariableList}
          placeholder={
            showWarning ? "El campo seleccionado cambió" : "Elegir campo"
          }
          selectedVariable={selectedVariable}
          onSelectVariable={onUserSelectVariable}
          triggerGroup={
            normalizedOperation?.needsComparativeValue ? "up-left" : "left"
          }
        />

        <OperationSelector
          selectedVariable={selectedVariable}
          selectedOperation={selectedOperationCondition}
          onSelectOperation={onUserSelectOperation}
          group={
            normalizedOperation?.needsComparativeValue ? "up-right" : "right"
          }
          disabled={!selectedVariable}
        />
      </GroupTextFieldRow>

      {needsComparative && (
        <GroupTextFieldRow>
          <div className="flex w-full overflow-hidden rounded-b-xl border-b border-l border-r border-neutral">
            <Button
              variant={null}
              onClick={() =>
                setConditionerType((prev) => (prev === "inp" ? "var" : "inp"))
              }
              disabled
              className="h-full rounded-bl-xl hidden rounded-r-none rounded-tl-none border-b-0 border-l-0 border-t-0 border-neutral font-semibold"
            >
              <pre>{conditionerType}</pre>
            </Button>

            {conditionerType === "inp" ? (
              <>
                {selectedVariable?.type === "datetime" ||
                selectedVariable?.type === "date" ||
                selectedVariable?.type === "date_range" ||
                selectedVariable?.type === "datetime_range" ? (
                  <ComparativeValueInputCalendar
                    className="border-0 rounded-none"
                    value={dateValue as string | undefined}
                    onChangeDate={(val) => {
                      setDateValue(val);
                    }}
                  />
                ) : (
                  <Input
                    onChange={(e) => {
                      const v = e.target.value;
                      setInputField(v);
                    }}
                    type={
                      selectedVariable?.type === "text" ||
                      selectedVariable?.type === "email" ||
                      selectedVariable?.type === "textarea" ||
                      selectedVariable?.type === "phone" ||
                      selectedVariable?.type === "identification" ||
                      selectedVariable?.type === "select"
                        ? "text"
                        : "number"
                    }
                    value={inputField}
                    placeholder={
                      selectedVariable?.type === "text"
                        ? "Escribe un texto"
                        : selectedVariable?.type === "select"
                          ? "Opcion 1 / Opcion 2 / Opcion 3"
                          : "Escribe un valor"
                    }
                    className="w-full border-0"
                  />
                )}
              </>
            ) : (
              <Input
                onChange={(e) => setInputField(e.target.value)}
                type={selectedVariable?.type === "text" ? "text" : "number"}
                value={inputField}
                placeholder={
                  selectedVariable?.type === "text"
                    ? "Selecciona una variable o escribe un valor"
                    : "Selecciona una variable o escribe un número"
                }
                className="w-full border-0"
              />
            )}
          </div>
        </GroupTextFieldRow>
      )}
    </GroupTextField>
  );
};
